import ISearchParams from '@shared/models/ISearchParams';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import AlertsContainer from './state/AlertsContainer';
import OrganizationContainer from './state/OrganizationContainer';
import useScrollToElement from './useScrollToElement';
import useCurrentOrgDetailsFetching from './useCurrentOrgDetailsFetching';

export default function useCurrentOrgMemberSearch() {
    const { t } = useTranslation();
    const { currentOrg } = useCurrentOrgDetailsFetching();
    const { showError } = AlertsContainer.useContainer();
    const organizationContainer = OrganizationContainer.useContainer();
    const orgMembersSearchContentId = 'org-members-search';
    const defaultTake = 51;
    const displayLimit = defaultTake - 1;
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [debouncedQuery, setDebouncedQuery] = useState<ISearchParams>({
        searchText: '',
        skip: 0,
        take: defaultTake,
    });
    const members = useMemo(() => {
        if (!currentOrg) return null;
        return (
            organizationContainer.organizationMembersRef.current.find((org) => org.organizationId === currentOrg.id)
                ?.members ?? []
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentOrg, organizationContainer.organizationMembers]);

    useScrollToElement(orgMembersSearchContentId, [debouncedQuery.skip]);

    function handleSearchQueryChange(value: string) {
        setSearchQuery(value);
    }

    const showPrevious = useMemo(() => debouncedQuery.skip && debouncedQuery.skip > 0, [debouncedQuery.skip]);
    const showNext = useMemo(
        () => organizationContainer.organizationMembers.length >= displayLimit,
        [organizationContainer.organizationMembers]
    );

    const DEBOUNCE_DELAY = 500;

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const newQuery = {
                searchText: searchQuery,
                skip: 0,
                take: defaultTake,
            };
            if (newQuery.searchText !== debouncedQuery.searchText) {
                setDebouncedQuery(newQuery);
            }
        }, DEBOUNCE_DELAY);

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    const handleNext = () => {
        const count = organizationContainer.organizationMembersRef.current.length;
        setDebouncedQuery((prev) => ({
            ...prev,
            take: defaultTake,
            skip: displayLimit + (prev.skip ?? 0),
        }));
    };

    const handlePrevious = () => {
        const count = organizationContainer.organizationMembersRef.current.length;
        setDebouncedQuery((prev) => ({
            ...prev,
            take: defaultTake,
            skip: Math.max(0, (prev.skip ?? 0) - displayLimit),
        }));
    };

    useEffect(() => {
        const trySearch = async () => {
            if (organizationContainer.loadingDataRef.current.organizationMembers) return;
            if (!currentOrg) return;
            try {
                organizationContainer.handleLoadingDataChange('organizationMembers', true);
                await organizationContainer.loadOrganizationMembers(currentOrg.id, debouncedQuery);
            } catch (e) {
                showError(t('common:organization.member.loadError'));
            } finally {
                organizationContainer.handleLoadingDataChange('organizationMembers', false);
            }
        };
        trySearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedQuery]);

    return {
        results: members,
        orgMembersSearchContentId,
        handleSearchQueryChange,
        searchQuery,
        showPrevious,
        showNext,
        handleNext,
        handlePrevious,
        isLoading: organizationContainer.loadingData.organizationMembers,
    };
}
